import { z } from 'zod';
import { lenientUuid } from '../schema-extensions';
import { ProtectionTargetType } from '../enums/protection-target-type.enum';
import { ItemProtectionBenefitType } from '../enums/item-protection-benefit-type.enum';
import { PetProtectionBenefitType } from '../enums/pet-protection-benefit-type.enum';
import { PersonProtectionBenefitType } from '../enums/person-protection-benefit-type.enum';
import { PropertyProtectionBenefitType } from '../enums/property-protection-benefit-type.enum';
import { VehicleProtectionBenefitType } from '../enums/vehicle-protection-benefit-type.enum';

// ============================================================================
// LENIENT SCHEMAS (for deserialization)
// ============================================================================
const lenientItemProtectionSchema = z.object({
  protectionType: z.literal(ProtectionTargetType.ITEM),
  id: lenientUuid(),
  benefits: z.array(z.nativeEnum(ItemProtectionBenefitType)),
}).transform(val => ({
  protectionType: val.protectionType,
  id: val.id,
  benefits: val.benefits,
}));

const lenientPetProtectionSchema = z.object({
  protectionType: z.literal(ProtectionTargetType.PET),
  id: lenientUuid(),
  benefits: z.array(z.nativeEnum(PetProtectionBenefitType)),
}).transform(val => ({
  protectionType: val.protectionType,
  id: val.id,
  benefits: val.benefits,
}));

const lenientPersonProtectionSchema = z.object({
  protectionType: z.literal(ProtectionTargetType.PERSON),
  id: lenientUuid(),
  benefits: z.array(z.nativeEnum(PersonProtectionBenefitType)),
}).transform(val => ({
  protectionType: val.protectionType,
  id: val.id,
  benefits: val.benefits,
}));

const lenientPropertyProtectionSchema = z.object({
  protectionType: z.literal(ProtectionTargetType.PROPERTY),
  id: lenientUuid(),
  benefits: z.array(z.nativeEnum(PropertyProtectionBenefitType)),
}).transform(val => ({
  protectionType: val.protectionType,
  id: val.id,
  benefits: val.benefits,
}));

const lenientVehicleProtectionSchema = z.object({
  protectionType: z.literal(ProtectionTargetType.VEHICLE),
  id: lenientUuid(),
  benefits: z.array(z.nativeEnum(VehicleProtectionBenefitType)),
}).transform(val => ({
  protectionType: val.protectionType,
  id: val.id,
  benefits: val.benefits,
}));

const lenientProtectionReferenceNodeSchema = z.union([
  lenientItemProtectionSchema,
  lenientPetProtectionSchema,
  lenientPersonProtectionSchema,
  lenientPropertyProtectionSchema,
  lenientVehicleProtectionSchema,
]);

// ============================================================================
// STRICT SCHEMAS (for validation)
// ============================================================================
const strictItemProtectionSchema = z.object({
  protectionType: z.literal(ProtectionTargetType.ITEM),
  id: z.string().uuid('ID must be a valid UUID'),
  benefits: z.array(z.nativeEnum(ItemProtectionBenefitType, { errorMap: () => ({ message: 'Invalid item protection benefit type' }) })).min(1, 'At least one benefit is required'),
}).transform(val => ({
  protectionType: val.protectionType,
  id: val.id,
  benefits: val.benefits,
}));

const strictPetProtectionSchema = z.object({
  protectionType: z.literal(ProtectionTargetType.PET),
  id: z.string().uuid('ID must be a valid UUID'),
  benefits: z.array(z.nativeEnum(PetProtectionBenefitType, { errorMap: () => ({ message: 'Invalid pet protection benefit type' }) })).min(1, 'At least one benefit is required'),
}).transform(val => ({
  protectionType: val.protectionType,
  id: val.id,
  benefits: val.benefits,
}));

const strictPersonProtectionSchema = z.object({
  protectionType: z.literal(ProtectionTargetType.PERSON),
  id: z.string().uuid('ID must be a valid UUID'),
  benefits: z.array(z.nativeEnum(PersonProtectionBenefitType, { errorMap: () => ({ message: 'Invalid person protection benefit type' }) })).min(1, 'At least one benefit is required'),
}).transform(val => ({
  protectionType: val.protectionType,
  id: val.id,
  benefits: val.benefits,
}));

const strictPropertyProtectionSchema = z.object({
  protectionType: z.literal(ProtectionTargetType.PROPERTY),
  id: z.string().uuid('ID must be a valid UUID'),
  benefits: z.array(z.nativeEnum(PropertyProtectionBenefitType, { errorMap: () => ({ message: 'Invalid property protection benefit type' }) })).min(1, 'At least one benefit is required'),
}).transform(val => ({
  protectionType: val.protectionType,
  id: val.id,
  benefits: val.benefits,
}));

const strictVehicleProtectionSchema = z.object({
  protectionType: z.literal(ProtectionTargetType.VEHICLE),
  id: z.string().uuid('ID must be a valid UUID'),
  benefits: z.array(z.nativeEnum(VehicleProtectionBenefitType, { errorMap: () => ({ message: 'Invalid vehicle protection benefit type' }) })).min(1, 'At least one benefit is required'),
}).transform(val => ({
  protectionType: val.protectionType,
  id: val.id,
  benefits: val.benefits,
}));

const strictProtectionReferenceNodeSchema = z.union([
  strictItemProtectionSchema,
  strictPetProtectionSchema,
  strictPersonProtectionSchema,
  strictPropertyProtectionSchema,
  strictVehicleProtectionSchema,
]);

// ============================================================================
// TYPE
// ============================================================================
export type ProtectionReferenceNode = z.infer<typeof lenientProtectionReferenceNodeSchema>;

// ============================================================================
// NAMESPACE (for Methods)
// ============================================================================
export namespace ProtectionReferenceNode {
  export function deserialize(data: unknown): ProtectionReferenceNode {
    return lenientProtectionReferenceNodeSchema.parse(data);
  }

  export function create(data: unknown): ProtectionReferenceNode {
    return strictProtectionReferenceNodeSchema.parse(data);
  }
}
